<template>
  <div class="one_step">    
    <toast :text="goodNews()"/>    <!-- #pjw -->
    <div class="step_shell for_pb">
      <h2 class="details_header">{{ header }}</h2>
      <div class="field_wrap textfield_wrap address_lookup_wrap">
        <span class="field_label">What is the address of the farm?</span>
        <v-text-field
          data-test-id="step-2-farm-address"
          outlined
          v-model="Farm_Address_Text"
          color="#0200b9"
          :rules="name_rules"
          placeholder="Start typing..."
          class="text_field"
          v-on:input="lookupAddress"
          :class="{ second_field_shown: secondAddressShown }"
          v-click-outside="cleanLookupSuggestions"
        />
        <div
          class="address_suggestions"
          v-click-outside="cleanLookupSuggestions"
        >
          <ul>
            <li
              v-for="(item, index) of address_suggestions_1"
              :key="'1_' + index"
            >
              <button
                v-on:click="selectAddress1(item.id, item.label)"
                class="suggestion_button"
              >
                {{ item.label }}
              </button>
            </li>
          </ul>
        </div>

        <v-text-field
          v-if="secondAddressShown"
          outlined
          v-model="NewFarm_Address_Text"
          color="#0200b9"
          :rules="name_rules"
          placeholder="Start typing..."
          class="text_field"
          v-on:input="lookupAddress2"
          v-click-outside="cleanLookupSuggestions2"
        />
        <div
          class="address_suggestions second_suggestions"
          v-if="secondAddressShown"
          v-click-outside="cleanLookupSuggestions2"
        >
          <ul>
            <li
              v-for="(item, index) of address_suggestions_2"
              :key="'2_' + index"
            >
              <button
                v-on:click="selectAddress2(item.id, item.label)"
                class="suggestion_button"
              >
                {{ item.label }}
              </button>
            </li>
          </ul>
        </div>

        <div class="second_address_control">
          <button
            v-if="secondAddressShown"
            class="text_link"
            v-on:click="removeSecondAddress"
          >
            - <span>Remove address</span>
          </button>
          <button v-else class="text_link" v-on:click="showSecondAddress">
            + <span>Add another address</span>
          </button>
        </div>
      </div>

      <div class="field_wrap">
        <span class="field_label"
          >Is the farm 50ha or larger?
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <button class="tooltip_button" v-bind="attrs" v-on="on"></button>
            </template>
            <span
              >If you're refinancing and buying new, both farms must be 50ha or
              larger</span
            >
          </v-tooltip>
        </span>
        <v-radio-group
          data-test-id="step-2-far-bigger-50"
          class="radio_place"
          v-model="Farm_Bigger_50"
          row
          required
        >
          <v-radio
            v-for="n in 2"
            v-bind:key="'Farm_Bigger_50_' + n"
            color="#0200bd"
            :label="getRadioLabel(n)"
            :value="getRadioValue(n)"
            required
          ></v-radio>
        </v-radio-group>
      </div>

      <div class="field_wrap">
        <span class="field_label">What type of farm do you operate?</span>
        <div class="buttons_wrap_4">
          <!-- #pjw v-if below -->
          <image-button
            v-if="product === 'beef'"
            data-test-id="step-2-farm-type-beef"
            :selected="isTypeSelected('beef')"
            value="beef"
            field="farm_type"
            v-on="$listeners"
          />
          <image-button
            v-if="product === 'dairy'"
            data-test-id="step-2-farm-type-dairy"
            :selected="isTypeSelected('dairy')"
            value="dairy"
            field="farm_type"
            v-on="$listeners"
          />
          <image-button
            v-if="product === 'beef'"
            data-test-id="step-2-farm-type-sheep"
            :selected="isTypeSelected('sheep')"
            value="sheep"
            field="farm_type"
            v-on="$listeners"
          />
          <image-button
            v-if="product === 'beef'"
            data-test-id="step-2-farm-type-both"
            :selected="isTypeSelected('both')"
            value="both"
            field="farm_type"
            v-on="$listeners"
          />
          <image-button
            data-test-id="step-2-farm-type-other"
            :selected="isTypeSelected('other')"
            value="other"
            field="farm_type"
            v-on="$listeners"
          />
        </div>
      </div>

      <!-- #pjw -->
      <div class="field_wrap" v-if="product==='dairy' && isTypeSelected('dairy')">
        <span class="field_label"
          >Does the farm currently have the necessary consents?          
        </span>
        <v-radio-group
          data-test-id="step-2-farm-has-consents"
          class="radio_place"
          v-model="Farm_Has_Consents"
          row
          required
        >
          <v-radio
            v-for="n in 2"
            v-bind:key="'Farm_Has_Consents_' + n"
            color="#0200bd"
            :label="getRadioLabel(n)"
            :value="getRadioValue(n)"
            required
          ></v-radio>
        </v-radio-group>
      </div>

      <div class="field_wrap">
        <span class="field_label"
          >What entity owns the farm (or who will own the farm)?</span
        >
        <div class="buttons_wrap_4">
          <image-button
            data-test-id="step-2-entity-type-trust"
            :selected="isSelected('trust')"
            value="trust"
            field="owner_type"
            v-on="$listeners"
          />
          <image-button
            data-test-id="step-2-entity-type-individual"
            :selected="isSelected('individual')"
            value="individual"
            field="owner_type"
            v-on="$listeners"
          />
          <image-button
            data-test-id="step-2-entity-type-partnership"
            :selected="isSelected('partnership')"
            value="partnership"
            field="owner_type"
            v-on="$listeners"
          />
          <image-button
            data-test-id="step-2-entity-type-company"
            :selected="isSelected('company')"
            value="company"
            field="owner_type"
            v-on="$listeners"
          />
        </div>
      </div>

      <div class="field_wrap textfield_wrap">
        <span class="field_label"
          >Name of {{ selectedType("owner_type") }}</span
        >
        <v-text-field
          data-test-id="step-2-entity-name"
          outlined
          v-model="Farm_Owner_Name"
          color="#0200b9"
          :rules="name_rules"
          class="text_field"
          maxlength="30"
        />
      </div>

      <div class="field_wrap">
        <span class="field_label"
          >Does/will the same entity operate the farm?</span
        >
        <v-radio-group
          data-test-id="step-2-entity-operates-farm"
          class="radio_place"
          v-model="sameEntityOperatesTheFarm"
          row
          required
        >
          <v-radio
            v-for="n in 2"
            v-bind:key="'sameEntityOperatesTheFarm' + n"
            color="#0200bd"
            :label="getRadioLabel(n)"
            :value="getRadioValue(n)"
            required
          ></v-radio>
        </v-radio-group>
      </div>

      <div class="field_wrap" v-if="!sameEntityOperatesTheFarm">
        <span class="field_label"
          >What entity operates/will operate the farm?</span
        >
        <div class="buttons_wrap_4">
          <image-button
            :selected="isOperatorSelected('trust')"
            value="trust"
            field="operator_type"
            v-on="$listeners"
          />
          <image-button
            :selected="isOperatorSelected('individual')"
            value="individual"
            field="operator_type"
            v-on="$listeners"
          />
          <image-button
            :selected="isOperatorSelected('partnership')"
            value="partnership"
            field="operator_type"
            v-on="$listeners"
          />
          <image-button
            :selected="isOperatorSelected('company')"
            value="company"
            field="operator_type"
            v-on="$listeners"
          />
        </div>
      </div>

      <div class="field_wrap textfield_wrap" v-if="!sameEntityOperatesTheFarm">
        <span class="field_label"
          >Name of {{ selectedType("operator_type") }}</span
        >
        <v-text-field
          outlined
          v-model="Farm_Operator_Name"
          color="#0200b9"
          :rules="name_rules"
          class="text_field"
          maxlength="30"
        />
      </div>

      <div class="field_wrap">
        <span class="field_label">
          Are you completing this application as a shareholder, owner, or
          director of the farm or farm entity?
        </span>
        <v-radio-group
          data-test-id="step-2-entity-authorized"
          class="radio_place"
          v-model="Applicant_Autorized"
          row
          required
        >
          <v-radio
            v-for="n in 2"
            v-bind:key="'Applicant_Autorized' + n"
            color="#0200bd"
            :label="getRadioLabel(n)"
            :value="getRadioValue(n)"
            required
          ></v-radio>
        </v-radio-group>
      </div>

      <div class="field_wrap" v-if="!Applicant_Autorized">
        <span class="field_label">
          Are you authorised to run a credit check on behalf of the shareholder,
          owner or director?
        </span>
        <v-radio-group
          data-test-id="step-2-entity-authorized_credit"
          class="radio_place"
          v-model="Authorised_Credit_Check"
          row
          required
        >
          <v-radio
            v-for="n in 2"
            v-bind:key="'Authorised_Credit_Check' + n"
            color="#0200bd"
            :label="getRadioLabel(n)"
            :value="getRadioValue(n)"
            required
          ></v-radio>
        </v-radio-group>
      </div>
    </div>
  </div>
</template>

<script>
import vClickOutside from "v-click-outside";
import libraryMixin from "./library_mixin";
import ImageButton from "@/components/ImageButton.vue";
import Toast from "@/components/Toast.vue";

export default {
  mixins: [libraryMixin],
  components: {
    imageButton: ImageButton,
    toast: Toast,
  },
  directives: {
    clickOutside: vClickOutside.directive,
  },
  props: {
    current: {
      type: Boolean,
      default: false,
    },
    header: {
      type: String,
      default: "",
    },
    //#pjw 'Dairy Direct' or 'Beef & Sheep Direct'
    appName: {
      type: String,
      default: "",
    },    
  },
  data() {
    return {
      stepIndex: 2,
      valid: false,
      Farm_Address_Text: null,
      Farm_Address_Details: {},
      NewFarm_Address_Text: null,
      NewFarm_Address_Details: {},
      Farm_Bigger_50: true,
      Farm_Owner_Name: null,
      Farm_Operator_Name: null,
      Applicant_Autorized: true,
      Authorised_Credit_Check: true,
      sameEntityOperatesTheFarm: true,
      showToast: false,
      address_suggestions_1: [],
      address_suggestions_2: [],
      secondAddressShown: false,
      //#pjw new fields
      product: null, 
      Farm_Has_Consents:null,
    };
  },
  watch: {
    current: {
      handler(val) {
        if (val) {
          this.showToast = true;
        }
      },
    },
  },
  computed: {       
    farmInfo: function clientInfo() {
      return {
        Farm_Address_Text: this.Farm_Address_Text,
        Farm_Address_Details: this.Farm_Address_Details,
        NewFarm_Address_Text: this.NewFarm_Address_Text,
        NewFarm_Address_Details: this.NewFarm_Address_Details,
        Farm_Bigger_50: this.Farm_Bigger_50,
        Farm_Has_Consents:this.Farm_Has_Consents, //#pjw
        Farm_Owner_Name: this.Farm_Owner_Name,
        Farm_Operator_Name: this.Farm_Operator_Name
          ? this.Farm_Operator_Name
          : this.Farm_Owner_Name,
        Applicant_Autorized: this.Applicant_Autorized,
        Authorised_Credit_Check: this.Authorised_Credit_Check,
        sameEntityOperatesTheFarm: this.sameEntityOperatesTheFarm,
      };
    },
  },
  updated: function() {
    this.debouncedDataUpdate();
    setTimeout(() => {
      this.showToast = false;
    }, 10000);
  },
  methods: {
    goodNews: function goodNews() {
      return 'Good news - you may be eligible for a ' + this.appName + ' loan.';
      //return (this.product === 'dairy' ? 'Good news - you may be eligible for a Dairy Direct loan.': 'Good news - you may be eligible for a Sheep & Beef Direct loan.')
    }, 
    updateData: function updateData() {
      this.valid = this.checkValidity();
      this.$emit("updateFarmInfo", this.farmInfo);
      this.$emit("stepValid", { step: 2, valid: this.valid });
      this.$emit("sendDataToAPI");
    },
    checkValidity: function checkValidity() {
    
      var this_data = [!!this.Farm_Address_Text, !!this.Farm_Owner_Name];
      var isvalid = this_data.every(function(el) {
        return el;
      });

      if (!this.sameEntityOperatesTheFarm && !this.Farm_Operator_Name) {
        isvalid = false;
      }
      if (this.secondAddressShown && !this.NewFarm_Address_Text) {
        isvalid = false;
      }

      //#pjw
      if (this.product==='dairy' && this.isTypeSelected('dairy') && !this.hasConsentsSelected()) {
        isvalid = false;
      }
      return isvalid;
    },
    cleanAddress_details: function cleanAddress_details(index) {
      if (index === 1) {
        this.Farm_Address_Details = {};
      } else {
        this.NewFarm_Address_Details = {};
      }
    },
    fillAddress_details: function fillAddress_details(data, index) {
      if (index === 1) {
        this.Farm_Address_Details = data;
      } else {
        this.NewFarm_Address_Details = data;
      }
    },
    selectAddress1: function selectAddress1(addressId, addressString) {
      this.Farm_Address_Text = addressString;
      this.makeDetailedAddressCall(addressId, 1);
      this.address_suggestions_1 = [];
      this.debouncedDataUpdate();
    },
    selectAddress2: function selectAddress2(addressId, addressString) {
      this.NewFarm_Address_Text = addressString;
      this.makeDetailedAddressCall(addressId, 2);
      this.address_suggestions_2 = [];
      this.debouncedDataUpdate();
    },
    showSecondAddress: function showSecondAddress() {
      this.secondAddressShown = true;
    },
    removeSecondAddress: function removeSecondAddress() {
      this.secondAddressShown = false;
      this.NewFarm_Address_Text = null;
      this.address_suggestions_2 = [];
      this.NewFarm_Address_Details = {};
      this.updateData();
    },
    isSelected: function isSelected(str) {
      return this.$parent.$parent.application.owner_type === str;
    },
    isTypeSelected: function isTypeSelected(str) {
      return this.$parent.$parent.application.farm_type === str;
    },
    //#pjw hasConsentsSelected
    hasConsentsSelected:function hasConsentsSelected() {
      return this.$parent.$parent.application.farmInfo.Farm_Has_Consents != null;
    },
    isOperatorSelected: function isOperatorSelected(str) {
      return this.$parent.$parent.application.operator_type === str;
    },
    selectedType: function selectedType(str) {
      switch (this.$parent.$parent.application[str]) {
        case "trust":
          return "Trust";
          break;
        case "individual":
          return "Individual";
          break;
        case "partnership":
          return "Partnership";
          break;
        case "company":
          return "Company";
          break;
        default:
          return "";
      }
    },
  },
  created: function() {
    var app = this.$parent.$parent.application;
    var app_data = this.$parent.$parent.application.farmInfo;

    this.product = app.product; //#pjw
    //console.log("Step2.vue product=" + this.product + " appName=" + this.appName); 

    this.Farm_Address_Text = app_data.Farm_Address_Text
      ? app_data.Farm_Address_Text
      : null;
    this.Farm_Address_Details = app_data.Farm_Address_Details
      ? app_data.Farm_Address_Details
      : null;
    this.NewFarm_Address_Text = app_data.NewFarm_Address_Text
      ? app_data.NewFarm_Address_Text
      : null;
    this.NewFarm_Address_Details = app_data.NewFarm_Address_Details
      ? app_data.NewFarm_Address_Details
      : null;
    this.Farm_Bigger_50 =
      typeof app_data.Farm_Bigger_50 === "undefined"
        ? true
        : app_data.Farm_Bigger_50;
    this.Farm_Owner_Name = app_data.Farm_Owner_Name
      ? app_data.Farm_Owner_Name
      : null;
    this.Farm_Operator_Name = app_data.Farm_Operator_Name
      ? app_data.Farm_Operator_Name
      : null;
    this.Applicant_Autorized =
      typeof app_data.Applicant_Autorized === "undefined"
        ? true
        : app_data.Applicant_Autorized;
    this.Authorised_Credit_Check =
      typeof app_data.Authorised_Credit_Check === "undefined"
        ? true
        : app_data.Authorised_Credit_Check;
    this.sameEntityOperatesTheFarm =
      typeof app_data.sameEntityOperatesTheFarm === "undefined"
        ? true
        : app_data.Applicant_Autorized;
  },
};
</script>
